import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { browserHistory } from '@americanexpress/one-app-router';
import { callFxgApi, FxComponentWrapper } from 'axp-fx-common';
import { apiPaths, routes } from '../../constants/paths';
import { httpStatus } from '../../constants/responseCodes';
import { SIGN_UP_EXPERIENCE } from '../../constants/experiences';
import {
  loadExperience,
  closeSessionFrame,
  setOptions,
  isAmexSessionReady,
} from '../../util/amexSession';
import RegisterLayout from './RegisterLayout';
import Welcome from './Registration/Welcome';
import RegistrationSuccessPage from './Registration/RegistrationSuccessPage';
import RegistrationAlmostTherePage from './Registration/RegistrationAlmostTherePage';
import RsvpErrorPage from './Registration/RsvpErrorPage';

// Route to the retryable error page whose "Try Again" reloads /register.
const pushRetryableError = () => {
  browserHistory.push({
    pathname: routes.error,
    state: { buttonLabel: 'Try Again', buttonHref: routes.register },
  });
};

const Register = () => {
  const fxgApiHost = useSelector((state) => state.getIn(['config', 'fxgApi']));
  const appBaseUrls = useSelector((state) => state.getIn(['config', 'appBaseUrl']));

  const [isRegistrationSuccess, setIsRegistrationSuccess] = useState(false);
  // Partial success: sign-up completed but the enrollment to SFT product failed.
  const [showAlmostThere, setShowAlmostThere] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  // Gate the page on the /welcome/authenticate check: the loader stays up until a
  // 200 flips isWelcomeAuthenticated (or a result page is reached). Every other
  // outcome navigates away while the loader is still showing.
  const [isWelcomeAuthenticated, setIsWelcomeAuthenticated] = useState(false);
  const [showRsvpError, setShowRsvpError] = useState(false);

  /**
   * On page load, set the en-US locale on AmexSession so the enterprise
   * experience (Smart Sign-Up) renders in en-US. AmexSession is provided by the
   * shared script and may not be ready when this page mounts, so poll until it
   * is, set the locale once, then stop.
   */
  useEffect(() => {
    const timer = setInterval(() => {
      if (!isAmexSessionReady()) return;
      clearInterval(timer);
      setOptions({ locale: 'en-US' });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  /**
   * Validate the registration on page load via /welcome/authenticate.
   * Keyed on HTTP status:
   *  - 200 → registration in progress; mark authenticated and show Welcome content.
   *  - 400 / 404 → registration cannot continue (completed / expired / cancelled /
   *    not found); show the RSVP "unable to continue registration" page.
   *  - anything else (500 / unhandled / thrown) → route to the retryable error page.
   *
   * Only a 200 sets isWelcomeAuthenticated; every other path keeps the loader up
   * (rather than briefly flashing the Welcome page) until the RSVP/error view
   * takes over.
   */
  useEffect(() => {
    const authenticateWelcome = async () => {
      try {
        const { status } = await callFxgApi(
          `${fxgApiHost}${apiPaths.welcomeAuthenticate}`,
          'POST',
          null
        );

        if (status === httpStatus.OK) {
          setIsWelcomeAuthenticated(true);
          return;
        }

        if (status === httpStatus.BAD_REQUEST || status === httpStatus.NOT_FOUND) {
          setShowRsvpError(true);
        } else {
          pushRetryableError();
        }
      } catch (error) {
        window.console.error('welcome authenticate failed', error);
        pushRetryableError();
      }
    };

    authenticateWelcome();
  }, [fxgApiHost]);

  /**
   * Enrollment to SFT product for the user after a successful sign-up.
   *  - HTTP 200 → registration successful; show the success page.
   *  - any other failure (non-200 or thrown) → show the "Almost There" page
   *    prompting the user to log in with their newly created credentials.
   */
  const enrolUserInSft = async () => {
    setShowLoader(true);
    try {
      const { status: regStatus } = await callFxgApi(
        `${fxgApiHost}${apiPaths.productEnroll}`,
        'POST',
        null
      );

      if (regStatus === httpStatus.OK) {
        setIsRegistrationSuccess(true);
      } else {
        setShowAlmostThere(true);
      }
    } catch (error) {
      window.console.error('enrolUserInSft failed', error);
      setShowAlmostThere(true);
    } finally {
      setShowLoader(false);
    }
  };

  /**
   * "Sign Up" CTA — launch the enterprise Smart Sign-Up experience via
   * AmexSession.loadExperience for users creating a new account. Once it
   * completes:
   * - if the experience succeeds (statusCode === 0), run the enrollment to SFT product
   * - otherwise, route back to /register so the user can restart the sign-up
   */
  const onSignUpClick = () => {
    // Set the locale on AmexSession before launching the experience so the
    // enterprise Smart Sign-Up renders in en-US.
    setOptions({ locale: 'en-US' });
    loadExperience({
      ...SIGN_UP_EXPERIENCE,
      callback: ({ status, statusCode, errorCode }) => {
        window.console.log(status, statusCode, errorCode);
        closeSessionFrame();
        // Sign-up completed successfully when the experience reports statusCode === 0.
        if (statusCode === 0) {
          enrolUserInSft();
        } else {
          // Sign-up did not complete — send the user back to restart the flow.
          browserHistory.push({ pathname: routes.register });
        }
      },
    });
  };

  /**
   * "Log In" CTA — for users who already have American Express application
   * credentials. Send them straight to the FileXpress app to log in.
   */
  const onLoginClick = () => {
    window.location.assign(appBaseUrls);
  };

  /**
   *
   * @returns component to be displayed during registration flow
   *
   * On successful enrollment to SFT product, show the success page.
   * If sign-up succeeded but enrollment to SFT product failed, show the "Almost There" page.
   * Default return the Welcome page to the user.
   */
  const renderPage = () => {
    if (isRegistrationSuccess) {
      return <RegistrationSuccessPage />;
    }
    if (showAlmostThere) {
      return <RegistrationAlmostTherePage />;
    }
    return <Welcome onSignUpClick={onSignUpClick} onLoginClick={onLoginClick} />;
  };

  // Registration is not valid to continue — show the RSVP error page (it brings
  // its own banner layout).
  if (showRsvpError) {
    return <RsvpErrorPage />;
  }

  // The single-message result pages (and the loader) are vertically centered;
  // the Welcome page keeps its default top-aligned flow.
  const isResultPage = isRegistrationSuccess || showAlmostThere;

  // Keep the loader up until /welcome/authenticate succeeds (or a result page is
  // reached). This prevents the Welcome page from flashing while the authenticate
  // check is in flight or while a non-success outcome is navigating away.
  const isLoading = showLoader || (!isWelcomeAuthenticated && !isResultPage);

  return (
    <RegisterLayout centerContent={isResultPage || isLoading}>
      <FxComponentWrapper
        isLoading={isLoading}
        loadedComponent={renderPage()}
      />
    </RegisterLayout>
  );
};

export default Register;
